import React, { useEffect, useState } from "react";
import { Box, Button, ThemeProvider, CssBaseline } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import darkThemeWithCssVars from "../visuals/nanesaDarkTheme";
import lightThemeWithCssVars from "../visuals/nanesaLightTheme";
import { ExcelData } from "../../qrcode/ExcelData";
import handleDownload from "../../qrcode/handleImgDownload";
import { insertNewExcelData } from "../../qrcode/insertNewExcelData";
import QrCodeSection from "../../qrcode/QrCodeSection";
import { Call } from "../../../api/ApiCall";

export interface ExcelDataFormProps {
  twoColumns: boolean;
  excelData?: ExcelData | null | undefined;
  onQrCodePresent?: () => void;
  onCancel?: () => void;
  onUpdateShipments?: () => void;
}

const ShipmentForm: React.FC<ExcelDataFormProps> = ({ twoColumns, excelData: initialExcelData, onQrCodePresent, onCancel, onUpdateShipments }) => {
  const [formData, setFormData] = useState<ExcelData | null | undefined>(null);
  const [excelData, setExcelData] = useState<ExcelData | null>(null);
  const [pdfFiles, setPdfFiles] = useState<{ coa?: File; sds?: File; ddt?: File }>({});
  const theme = useTheme();

  const handleCancel = () => {
    setFormData(null);
    setExcelData(null);
    setPdfFiles({});
    if (onCancel) onCancel();
  };

  const handleSavePdf = async () => {
    if (!excelData) return;
    try {
      for (const type of ["coa", "sds", "ddt"] as const) {
        const file = pdfFiles[type];
        if (file) {
          const formData = new FormData();
          formData.append("pdfFile", file);
          formData.append("documentCode", excelData.code);
          formData.append("type", type);

          const response = await Call("pdf/upload", "post", formData);
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || "Errore durante l'upload del file");
          }
        }
      }
    } catch (err) {
      alert("Errore durante il caricamento dei PDF o dei dati.");
      console.error(err);
    }
  };

  const handleQrCodeData = (url: string, extractedData: ExcelData) => {
    setFormData({
      ...formData,
      qrCodeUrl: url,
      ...extractedData,
    } as ExcelData);
    setExcelData(extractedData);
    if (onQrCodePresent) onQrCodePresent();
  };

  const handlePdfFilesChange = (files: { coa?: File; sds?: File; ddt?: File }) => {
    setPdfFiles(files);
  };

  useEffect(() => {
    initialExcelData && setFormData(initialExcelData);
  }, [initialExcelData]);

  const savedMode = localStorage.getItem("themeMode");

  return (
    <ThemeProvider theme={savedMode === "dark" ? darkThemeWithCssVars : lightThemeWithCssVars}>
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "auto",
          padding: 3,
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        {/* QR Code Section */}
        <QrCodeSection onQrCodeGenerated={handleQrCodeData} isTwoColumns={twoColumns} onPdfFilesChange={handlePdfFilesChange} />

        <Box component="form" className={`fade-in ${excelData ? "visible" : "hidden"}`}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            {/* Left Buttons: Download and Print */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
              }}
            >
              {/* Download Button */}
              <Button
                sx={{
                  paddingRight: 3,
                  paddingLeft: 3,
                  backgroundColor: theme.palette.mode === "dark" ? "rgb(98, 98, 98)" : "rgb(239,239,239)",
                  color: theme.palette.mode === "dark" ? "#FFFFFF" : "#000000",
                }}
                onClick={() => handleDownload("qr-code")}
                variant="contained"
              >
                Download
              </Button>

              {/* Print Button */}
              <Button
                sx={{
                  paddingRight: 3,
                  paddingLeft: 3,
                  backgroundColor: theme.palette.mode === "dark" ? "rgb(98, 98, 98)" : "rgb(239,239,239)",
                  color: theme.palette.mode === "dark" ? "#FFFFFF" : "#000000",
                }}
                onClick={() => {
                  const qrCodeElement = document.getElementById("qr-code");
                  if (qrCodeElement) {
                    const printWindow = window.open("", "");
                    if (printWindow) {
                      printWindow.document.body.innerHTML = qrCodeElement.outerHTML;
                      printWindow.print();
                      printWindow.close();
                    }
                  }
                }}
                variant="contained"
              >
                Stampa
              </Button>
            </Box>

            {/* Right Button: Discard and Save */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              <Button variant="contained" color="error" onClick={handleCancel} sx={{ paddingRight: 3, paddingLeft: 3 }}>
                Annulla
              </Button>

              <Button
                sx={{
                  paddingRight: 3,
                  paddingLeft: 3,
                  backgroundColor: "rgb(190,209,113)",
                  "&:hover": {
                    color: "rgb(0,0,0)",
                  },
                }}
                variant="contained"
                onClick={async () => {
                  // Format the date to ensure consistent storage without timezone issues
                  const formatDateForStorage = (date: Date | string): string => {
                    if (typeof date === 'string') {
                      // If it's already a string, check if it's in YYYY-MM-DD format
                      if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
                        return date; // Already in correct format
                      }
                      // If it's another string format, try to parse it
                      const parsedDate = new Date(date);
                      if (isNaN(parsedDate.getTime())) {
                        return date; // Return as-is if can't parse
                      }
                      date = parsedDate; // Convert to Date for processing below
                    }
                    
                    // Handle Date object
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                  };

                  const cExcelData: ExcelData = {
                    code: excelData!.code,
                    articolo: excelData!.articolo,
                    lotto: excelData!.lotto,
                    dataProduzione: formatDateForStorage(excelData!.dataProduzione), // Store as YYYY-MM-DD string
                    quantita: excelData!.quantita,
                    coa: excelData!.coa,
                    numeroOrdine: excelData!.numeroOrdine,
                    numeroDdt: excelData!.numeroDdt,
                    cliente: excelData!.cliente,
                    sds: excelData!.sds,
                    coaPdfId: null,
                    sdsPdfId: null,
                    ddtPdfId: null,
                  };

                  await insertNewExcelData(cExcelData); // NEW Save Excel Data
                  await handleSavePdf(); // Save PDF Files
                  if (onUpdateShipments) onUpdateShipments(); // Update the shipments list
                }}
              >
                Salva
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default ShipmentForm;
